import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useSearchParams, Link } from 'react-router-dom'
import { motion } from 'framer-motion'
import { AlertCircle, CheckCircle2, Eye, EyeOff, Loader2, Lock } from 'lucide-react'
import { supabase } from '@/config/supabase'

export default function PasswordResetPage() {
  const { t } = useTranslation()
  const [searchParams] = useSearchParams()
  const [isValidToken, setIsValidToken] = useState(false)
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [showPassword, setShowPassword] = useState(false)
  const [showConfirmPassword, setShowConfirmPassword] = useState(false)
  const [newPassword, setNewPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [success, setSuccess] = useState<string | null>(null)

  useEffect(() => {
    const validateResetToken = async () => {
      // Hem query string'i hem de hash fragment'ı oku
      const qsAccess = searchParams.get('access_token')
      const qsRefresh = searchParams.get('refresh_token')
      const qsType = searchParams.get('type')

      const hash = typeof window !== 'undefined' ? window.location.hash : ''
      const hashParams = new URLSearchParams(hash?.startsWith('#') ? hash.slice(1) : '')
      const hAccess = hashParams.get('access_token')
      const hRefresh = hashParams.get('refresh_token')
      const hType = hashParams.get('type')

      const accessToken = (qsAccess || hAccess) || undefined
      const refreshToken = (qsRefresh || hRefresh) || undefined
      const type = (qsType || hType) || undefined

      try {
        if (accessToken && refreshToken && type === 'recovery') {
          const { error } = await supabase.auth.setSession({
            access_token: accessToken,
            refresh_token: refreshToken,
          })
          if (error) throw error
          setIsValidToken(true)
        } else {
          // Bazı durumlarda session zaten set edilmiş olabilir
          const { data: { session }, error } = await supabase.auth.getSession()
          if (error) throw error
          if (session?.user) {
            setIsValidToken(true)
          } else {
            setError('Geçersiz veya eksik şifre sıfırlama bağlantısı')
          }
        }
      } catch (err) {
        setError('Şifre sıfırlama linki geçersiz veya süresi dolmuş')
      } finally {
        setIsLoading(false)
      }
    }

    validateResetToken()
  }, [searchParams])

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError(null)
    setSuccess(null)

    if (newPassword.length < 8) {
      setError('Şifre en az 8 karakter olmalı')
      return
    }
    if (newPassword !== confirmPassword) {
      setError('Şifreler eşleşmiyor')
      return
    }

    setIsLoading(true)
    try {
      const { error } = await supabase.auth.updateUser({ password: newPassword })
      if (error) throw error
      setSuccess(t('auth.passwordResetSuccess'))
    } catch (err) {
      setError(err instanceof Error ? err.message : t('auth.passwordResetError'))
    } finally {
      setIsLoading(false)
    }
  }

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center px-4 py-8">
        <div className="w-full max-w-md card p-6 md:p-8 text-center">
          <Loader2 className="w-8 h-8 mx-auto animate-spin text-white/70" />
          <p className="text-white/70 mt-4">{t('common.loading')}</p>
        </div>
      </div>
    )
  }

  if (error || !isValidToken) {
    return (
      <div className="min-h-screen flex items-center justify-center px-4 py-8">
        <div className="w-full max-w-md card p-6 md:p-8 text-center">
          <AlertCircle className="w-10 h-10 mx-auto text-red-400" />
          <h2 className="text-xl font-semibold text-white mt-4">{t('auth.passwordResetTitle')}</h2>
          <p className="text-white/70 mt-2">{error || t('auth.passwordResetError')}</p>
          <div className="mt-6">
            <Link to="/forgot-password" className="btn-primary inline-flex items-center">
{t('auth.forgotPassword')}
            </Link>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen flex items-center justify-center px-4 py-8">
      <div className="w-full max-w-md">
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-8"
        >
          <h1 className="text-4xl md:text-5xl font-bold text-gradient mb-2">
            {t('auth.passwordResetTitle')}
          </h1>
          <p className="text-white/60 text-lg">
            {t('auth.passwordResetSubtitle')}
          </p>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="card p-6 md:p-8"
        >
          {success ? (
            <div className="flex items-center space-x-3 p-4 bg-emerald-500/20 border border-emerald-500/30 rounded-xl text-emerald-300">
              <CheckCircle2 className="w-5 h-5 flex-shrink-0" />
              <span className="text-sm">{success}</span>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-6">
              {error && (
                <div className="flex items-center space-x-3 p-4 bg-red-500/20 border border-red-500/30 rounded-xl text-red-300">
                  <AlertCircle className="w-5 h-5 flex-shrink-0" />
                  <span className="text-sm">{error}</span>
                </div>
              )}

              <div className="space-y-2">
                <label className="block text-white/80 text-sm font-medium">{t('auth.password')}</label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-white/40" />
                  <input
                    type={showPassword ? 'text' : 'password'}
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    className="input-field pl-11 pr-11"
                    placeholder={t('auth.newPasswordPlaceholder')}
                    required
                    minLength={8}
                    disabled={isLoading}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 transform -translate-y-1/2 text-white/40 hover:text-white/60 transition-colors"
                    disabled={isLoading}
                  >
                    {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                  </button>
                </div>
              </div>

              <div className="space-y-2">
                <label className="block text-white/80 text-sm font-medium">{t('auth.confirmPassword')}</label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-white/40" />
                  <input
                    type={showConfirmPassword ? 'text' : 'password'}
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    className="input-field pl-11 pr-11"
                    placeholder={t('auth.confirmPasswordPlaceholder')}
                    required
                    minLength={8}
                    disabled={isLoading}
                  />
                  <button
                    type="button"
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    className="absolute right-3 top-1/2 transform -translate-y-1/2 text-white/40 hover:text-white/60 transition-colors"
                    disabled={isLoading}
                  >
                    {showConfirmPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                  </button>
                </div>
              </div>

              <button
                type="submit"
                disabled={isLoading}
                className="btn-primary w-full flex items-center justify-center space-x-2"
              >
                {isLoading ? (
                  <>
                    <Loader2 className="w-5 h-5 animate-spin" />
                    <span>{t('auth.passwordResetSaving')}</span>
                  </>
                ) : (
                  <>
                    <Lock className="w-5 h-5" />
                    <span>{t('auth.passwordResetSave')}</span>
                  </>
                )}
              </button>
            </form>
          )}

          <div className="text-center mt-6">
            <p className="text-white/60 text-sm">
              {t('auth.forgotPasswordBackToLogin')} {' '}
              <Link to="/login" className="text-gradient hover:text-gradient-gold transition-colors font-medium">
                {t('auth.signIn')}
              </Link>
            </p>
          </div>
        </motion.div>
      </div>
    </div>
  )
}



